import { useState, useEffect } from 'react'

/**
 * Returns true once the user first interacts with the page (or after an idle
 * timeout), then never reverts. Use this to defer rendering of below-fold
 * components until the browser has spare capacity.
 *
 * Triggers: mousemove, touchstart, scroll, keydown
 * Fallback:  requestIdleCallback (timeout 4s) → setTimeout 4s
 */
const useInteractionReady = () => {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let fired = false
    let idleId = null

    const fire = () => {
      if (fired) return
      fired = true
      setReady(true)
      // Clean up remaining listeners once fired
      events.forEach(ev =>
        window.removeEventListener(ev, fire, { passive: true })
      )
      if (idleId !== null) {
        if (typeof cancelIdleCallback !== 'undefined') {
          cancelIdleCallback(idleId)
        } else {
          clearTimeout(idleId)
        }
      }
    }

    const events = ['mousemove', 'touchstart', 'scroll', 'keydown']
    events.forEach(ev =>
      window.addEventListener(ev, fire, { once: true, passive: true })
    )

    // Fallback: render even if the user never interacts
    if (typeof requestIdleCallback !== 'undefined') {
      idleId = requestIdleCallback(fire, { timeout: 4000 })
    } else {
      idleId = setTimeout(fire, 4000)
    }

    return () => {
      events.forEach(ev => window.removeEventListener(ev, fire))
      if (idleId !== null) {
        if (typeof cancelIdleCallback !== 'undefined') {
          cancelIdleCallback(idleId)
        } else {
          clearTimeout(idleId)
        }
      }
    }
  }, [])

  return ready
}

export default useInteractionReady
